/**
 * AWS Secrets Manager Service
 * Replaces Azure Key Vault
 *
 * Interface matches Azure Key Vault for easy migration
 */

import {
  SecretsManagerClient,
  GetSecretValueCommand,
  CreateSecretCommand,
  UpdateSecretCommand,
  DescribeSecretCommand,
} from '@aws-sdk/client-secrets-manager';

// Initialize Secrets Manager Client
function getSecretsClient(): SecretsManagerClient {
  const region = process.env.AWS_REGION || 'us-east-1';

  return new SecretsManagerClient({
    region,
    // AWS SDK automatically uses credentials
  });
}

/**
 * Get secret value from AWS Secrets Manager
 *
 * @param secretName - Secret identifier
 * @returns Secret value (string)
 */
export async function getSecret(secretName: string): Promise<string> {
  try {
    const client = getSecretsClient();

    const command = new GetSecretValueCommand({
      SecretId: secretName,
    });

    const response = await client.send(command);

    if (response.SecretString) {
      console.info(`✅ Retrieved secret: ${secretName}`);
      return response.SecretString;
    }

    if (response.SecretBinary) {
      // Convert binary to string
      const buffer = Buffer.from(response.SecretBinary);
      console.info(`✅ Retrieved binary secret: ${secretName}`);
      return buffer.toString('utf-8');
    }

    throw new Error(`Secret ${secretName} has no value`);
  } catch (error: any) {
    console.error(`❌ Error retrieving secret ${secretName}:`, error);
    throw error;
  }
}

/**
 * Get JSON secret from AWS Secrets Manager
 * Useful for secrets that store structured data
 *
 * @param secretName - Secret identifier
 * @returns Parsed JSON object
 */
export async function getSecretJSON<T = any>(secretName: string): Promise<T> {
  try {
    const secretValue = await getSecret(secretName);
    const parsed = JSON.parse(secretValue);
    console.info(`✅ Parsed JSON secret: ${secretName}`);
    return parsed;
  } catch (error) {
    console.error(`❌ Error parsing JSON secret ${secretName}:`, error);
    throw error;
  }
}

/**
 * Set/update secret value in AWS Secrets Manager
 *
 * @param secretName - Secret identifier
 * @param secretValue - Secret value (string or object)
 */
export async function setSecret(
  secretName: string,
  secretValue: string | object
): Promise<void> {
  try {
    const client = getSecretsClient();
    const valueString =
      typeof secretValue === 'string'
        ? secretValue
        : JSON.stringify(secretValue);

    // Check if secret exists
    try {
      await client.send(new DescribeSecretCommand({ SecretId: secretName }));

      // Secret exists, update it
      await client.send(
        new UpdateSecretCommand({
          SecretId: secretName,
          SecretString: valueString,
        })
      );

      console.info(`✅ Updated secret: ${secretName}`);
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        // Secret doesn't exist, create it
        await client.send(
          new CreateSecretCommand({
            Name: secretName,
            SecretString: valueString,
          })
        );

        console.info(`✅ Created secret: ${secretName}`);
      } else {
        throw error;
      }
    }
  } catch (error) {
    console.error(`❌ Error setting secret ${secretName}:`, error);
    throw error;
  }
}

/**
 * Database Credentials interface
 */
export interface DatabaseCredentials {
  username: string;
  password: string;
  host: string;
  port: string;
  database: string;
}

/**
 * Get database credentials from AWS Secrets Manager
 * Uses the flo/database/credentials secret
 *
 * @returns Database credentials object
 */
export async function getDatabaseCredentials(): Promise<DatabaseCredentials> {
  try {
    const secretName = process.env.DATABASE_SECRET_NAME || 'flo/database/credentials';
    const credentials = await getSecretJSON<DatabaseCredentials>(secretName);
    
    console.info(`✅ Retrieved database credentials from AWS Secrets Manager`);
    
    return {
      username: credentials.username,
      password: credentials.password,
      host: credentials.host,
      port: credentials.port || '1433',
      database: credentials.database,
    };
  } catch (error) {
    console.error('❌ Failed to retrieve database credentials from Secrets Manager:', error);
    throw error;
  }
}

/**
 * Test Secrets Manager connection
 */
export async function testSecretsConnection(): Promise<boolean> {
  try {
    const client = getSecretsClient();

    // Try to list secrets (requires ListSecrets permission)
    // If no permission, this will fail gracefully
    const testSecretName = process.env.AWS_TEST_SECRET_NAME || 'test-secret';

    try {
      await getSecret(testSecretName);
      console.info('✅ Secrets Manager connection successful');
      return true;
    } catch (error: any) {
      if (error.name === 'ResourceNotFoundException') {
        // Secret doesn't exist, but connection works
        console.info('✅ Secrets Manager connection successful (test secret not found, but that\'s OK)');
        return true;
      }
      throw error;
    }
  } catch (error) {
    console.error('❌ Secrets Manager connection failed:', error);
    return false;
  }
}